"use client";

/**
 * Motion system foundation (build-prompt Phase 4) — pure stage functions
 * driven by a swappable clock driver.
 *
 * CRITICAL ARCHITECTURE RULE (from the build prompt): every pipeline /
 * boardroom / Anchor animation is a PURE function of a 0..1 progress value
 * — `stageN(progress)` — and a separate DRIVER advances that progress.
 * Nothing in the animation may own a setTimeout timeline. This keeps the
 * production clock driver swappable for a scroll-scrub driver later
 * (driver swap, not a rebuild).
 *
 * This hook is the production CLOCK driver. It provides:
 *  (a) hover-to-pause            — listeners attached via the returned ref
 *  (b) clickable stage markers   — `goToStage(i)` jumps progress
 *  (c) off-viewport pause        — per-instance IntersectionObserver
 *  (d) reduced motion            — renders progress = 1 statically
 *
 * Usage sketch (a 4-stage pipeline):
 *
 *   const driver = useStageDriver({ durations: [4000, 3000, 5000, 2000] });
 *   // render = pure function of driver.frame:
 *   //   frame.progress       0..1 across the whole timeline
 *   //   frame.stage          current stage index
 *   //   frame.stageProgress  0..1 within that stage
 *   return (
 *     <section ref={driver.ref}>
 *       <StageRail active={driver.frame.stage} onSelect={driver.goToStage} />
 *       <PipelineSvg frame={driver.frame} />
 *     </section>
 *   );
 *
 * Perf escape hatch: pass `onFrame` to receive every rAF frame imperatively
 * (e.g. direct SVG attribute writes) and `reactStateFps` to throttle the
 * React state updates that drive declarative parts like the stage rail.
 *
 * The pure helper `frameAt(progress, durations)` is exported separately so
 * a future scrub driver (or a unit test) can compute identical frames.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefCallback,
} from "react";
import { usePrefersReducedMotion } from "./reduced-motion";

export interface StageFrame {
  /** 0..1 across the entire timeline. */
  progress: number;
  /** Index of the current stage. */
  stage: number;
  /** 0..1 within the current stage. */
  stageProgress: number;
}

export interface UseStageDriverOptions {
  /** Duration of each stage in ms. Array length = number of stages. */
  durations: number[];
  /** Restart from 0 when the timeline completes. Default true. */
  loop?: boolean;
  /** Start advancing immediately. Default true. */
  autoplay?: boolean;
  /** Pause while the pointer is over the ref'd element. Default false (continuous). */
  hoverPause?: boolean;
  /** Pause while the ref'd element is off-viewport. Default false (continuous). */
  viewportPause?: boolean;
  /** Imperative per-rAF-frame callback (bypasses React state). */
  onFrame?: (frame: StageFrame) => void;
  /**
   * Max React state updates per second for `frame`. Default 60 (every rAF).
   * Lower it (e.g. 10) when `onFrame` does the per-frame painting and React
   * state only drives coarse UI like the active stage marker.
   */
  reactStateFps?: number;
}

export interface StageDriver<T extends HTMLElement = HTMLElement> {
  /** Attach to the animated container — wires hover + viewport pause. */
  ref: RefCallback<T>;
  /** Current frame (React state — re-renders at `reactStateFps`). */
  frame: StageFrame;
  /** True when the clock is actually advancing. */
  playing: boolean;
  /** True under prefers-reduced-motion (frame is pinned to progress=1). */
  reducedMotion: boolean;
  play: () => void;
  pause: () => void;
  /** Jump progress to the start of stage `i` (clickable stage markers). */
  goToStage: (index: number) => void;
  /** Set raw progress 0..1 directly (the future scrub driver's entry point). */
  setProgress: (progress: number) => void;
}

/**
 * Pure: compute the stage frame for a global progress value, weighting
 * stages by their durations. Exported for tests + alternative drivers.
 */
export function frameAt(progress: number, durations: number[]): StageFrame {
  const p = Math.min(Math.max(progress, 0), 1);
  const total = durations.reduce((a, b) => a + b, 0);
  if (total <= 0 || durations.length === 0) {
    return { progress: p, stage: 0, stageProgress: p };
  }
  let elapsed = p * total;
  for (let i = 0; i < durations.length; i++) {
    if (elapsed <= durations[i] || i === durations.length - 1) {
      return {
        progress: p,
        stage: i,
        stageProgress: durations[i] > 0 ? Math.min(elapsed / durations[i], 1) : 1,
      };
    }
    elapsed -= durations[i];
  }
  return { progress: p, stage: durations.length - 1, stageProgress: 1 };
}

/** Pure: global progress at the start of stage `index`. */
export function progressAtStage(index: number, durations: number[]): number {
  const total = durations.reduce((a, b) => a + b, 0);
  if (total <= 0) return 0;
  const clamped = Math.min(Math.max(index, 0), durations.length - 1);
  let before = 0;
  for (let i = 0; i < clamped; i++) before += durations[i];
  return before / total;
}

export function useStageDriver<T extends HTMLElement = HTMLElement>(
  options: UseStageDriverOptions
): StageDriver<T> {
  const {
    durations,
    loop = true,
    autoplay = true,
    // Default OFF: marketing animations loop continuously, independent of
    // scroll position or hover — they must "just run, repeatedly" (per the
    // product requirement). Callers can opt back into pausing if needed.
    hoverPause = false,
    viewportPause = false,
    onFrame,
    reactStateFps = 60,
  } = options;

  const reducedMotion = usePrefersReducedMotion();
  const totalMs = useMemo(() => durations.reduce((a, b) => a + b, 0), [durations]);

  // --- mutable driver state (refs — the rAF loop must not close over stale state)
  const progressRef = useRef(0);
  const userPausedRef = useRef(!autoplay);
  const hoverPausedRef = useRef(false);
  const inViewRef = useRef(!viewportPause); // assume out of view until observed
  const rafRef = useRef<number | null>(null);
  const lastTsRef = useRef<number | null>(null);
  const lastStatePushRef = useRef(0);
  const durationsRef = useRef(durations);
  durationsRef.current = durations;
  const onFrameRef = useRef(onFrame);
  onFrameRef.current = onFrame;

  const [frame, setFrame] = useState<StageFrame>(() => frameAt(0, durations));
  const [playing, setPlaying] = useState(false);

  const shouldRun = useCallback((): boolean => {
    return (
      !reducedMotion &&
      !userPausedRef.current &&
      !hoverPausedRef.current &&
      inViewRef.current &&
      totalMs > 0
    );
  }, [reducedMotion, totalMs]);

  const pushFrame = useCallback(
    (force: boolean) => {
      const f = frameAt(progressRef.current, durationsRef.current);
      onFrameRef.current?.(f);
      const now = performance.now();
      const minInterval = 1000 / Math.max(reactStateFps, 1);
      if (force || now - lastStatePushRef.current >= minInterval) {
        lastStatePushRef.current = now;
        setFrame(f);
      }
    },
    [reactStateFps]
  );

  const stopLoop = useCallback(() => {
    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    lastTsRef.current = null;
    setPlaying(false);
  }, []);

  const startLoop = useCallback(() => {
    if (rafRef.current !== null || !shouldRun()) {
      setPlaying(rafRef.current !== null);
      return;
    }
    setPlaying(true);
    const tick = (ts: number) => {
      if (!shouldRun()) {
        stopLoop();
        return;
      }
      if (lastTsRef.current !== null) {
        const dt = ts - lastTsRef.current;
        let next = progressRef.current + dt / totalMs;
        if (next >= 1) {
          if (loop) {
            next = next % 1;
          } else {
            progressRef.current = 1;
            pushFrame(true);
            stopLoop();
            return;
          }
        }
        progressRef.current = next;
        pushFrame(false);
      }
      lastTsRef.current = ts;
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
  }, [shouldRun, stopLoop, pushFrame, totalMs, loop]);

  // --- public controls
  const play = useCallback(() => {
    userPausedRef.current = false;
    startLoop();
  }, [startLoop]);

  const pause = useCallback(() => {
    userPausedRef.current = true;
    stopLoop();
  }, [stopLoop]);

  const setProgress = useCallback(
    (p: number) => {
      progressRef.current = Math.min(Math.max(p, 0), 1);
      lastTsRef.current = null; // don't count paused time as elapsed
      pushFrame(true);
    },
    [pushFrame]
  );

  const goToStage = useCallback(
    (index: number) => {
      setProgress(progressAtStage(index, durationsRef.current));
      startLoop(); // resume from the marker if eligible to run
    },
    [setProgress, startLoop]
  );

  // --- reduced motion: pin to the completed end state, statically
  useEffect(() => {
    if (reducedMotion) {
      stopLoop();
      progressRef.current = 1;
      pushFrame(true);
    }
  }, [reducedMotion, stopLoop, pushFrame]);

  // --- ref wiring: hover pause + viewport pause on the container
  const cleanupRef = useRef<(() => void) | null>(null);
  const ref = useCallback(
    (node: T | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!node) return;

      const cleanups: Array<() => void> = [];

      if (hoverPause) {
        const onEnter = () => {
          hoverPausedRef.current = true;
          stopLoop();
        };
        const onLeave = () => {
          hoverPausedRef.current = false;
          startLoop();
        };
        node.addEventListener("mouseenter", onEnter);
        node.addEventListener("mouseleave", onLeave);
        cleanups.push(() => {
          node.removeEventListener("mouseenter", onEnter);
          node.removeEventListener("mouseleave", onLeave);
        });
      }

      if (viewportPause && typeof IntersectionObserver !== "undefined") {
        const io = new IntersectionObserver(
          ([entry]) => {
            inViewRef.current = entry.isIntersecting;
            if (entry.isIntersecting) startLoop();
            else stopLoop();
          },
          // threshold 0 + rootMargin: play whenever ANY part is near the
          // viewport, not just within a narrow 15%-visible band (which made
          // the loop stop when scrolling past a tall section).
          { threshold: 0, rootMargin: "150px 0px 150px 0px" }
        );
        io.observe(node);
        cleanups.push(() => io.disconnect());
      } else {
        inViewRef.current = true;
        startLoop();
      }

      cleanupRef.current = () => cleanups.forEach((fn) => fn());
    },
    [hoverPause, viewportPause, startLoop, stopLoop]
  );

  // --- unmount cleanup
  useEffect(() => {
    return () => {
      cleanupRef.current?.();
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return {
    ref,
    frame,
    playing,
    reducedMotion,
    play,
    pause,
    goToStage,
    setProgress,
  };
}
